import { ReactNode, useEffect, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import Form from "@atlaskit/form";
import { token } from "@atlaskit/tokens";

import { Adg4Button } from "@/components/adg4/button/src";
import { ProductKey } from "@/components/signup/utils";
import { useUserContext } from "@/components/user-context";
import { wacToken } from "@/libraries/design/foundations";
import { Jfind1315SubHeading } from "@/templates/try/cloud/signup/signup-page/src/logged-out/confluence/free/components/jfind-1315-subheading";
import { Jfind1249SubHeading } from "@/templates/try/cloud/signup/signup-page/src/logged-out/jira/free/components/jfind-1249-subheading";

import {
    ArrowLinkButton,
    InputSiteName,
    InputWorkspaceName
} from "../../../components";
import { useSiteNameContext } from "../../../site-name-context";
import { SitePickerField, WorkspacePickerField } from "../../site-picker-field";
import { WelcomeBackHeading } from "../../welcome-back-heading";

type Adg4SiteNameFormInputsProps = {
    isOnDarkBackground?: boolean;
};

export const Adg4SiteNameFormInputs = ({
    isOnDarkBackground = false
}: Adg4SiteNameFormInputsProps) => {
    const intl = useIntl();
    const {
        isNewUser,
        showSitePickerState: [showSitePicker, setShowSitePicker],
        productData: { productKey }
    } = useSiteNameContext();

    const { profile } = useUserContext();

    const [hasAvailableSites, setHasAvailableSites] = useState(false);

    let toggleYourSiteButtonText;
    if (productKey === ProductKey.CONFLUENCE) {
        toggleYourSiteButtonText = showSitePicker
            ? intl.formatMessage({
                  id: "BxpUiSignup.nameSite.createNewWorkspaceButton",
                  defaultMessage: "Create a new workspace"
              })
            : intl.formatMessage({
                  id: "BxpUiSignup.nameSite.visitExistingWorkspaceButton",
                  defaultMessage: "Visit an existing workspace"
              });
    } else {
        toggleYourSiteButtonText = showSitePicker
            ? intl.formatMessage({
                  id: "BxpUiSignup.nameSite.startNewSiteButton",
                  defaultMessage: "Start a new site"
              })
            : intl.formatMessage({
                  id: "BxpUiSignup.nameSite.useExistingSiteButton",
                  defaultMessage: "Use an existing site"
              });
    }

    useEffect(() => {
        if (!profile.requestInProgress) {
            const filteredAvailableSites =
                profile.attributes?.existingSites.filter(
                    ({ products }) =>
                        !products.some(
                            (product) => product.productKey === productKey
                        )
                ) ?? [];
            setHasAvailableSites(filteredAvailableSites.length > 0);
            setShowSitePicker(filteredAvailableSites.length > 0);
        }
    }, [
        profile.attributes?.existingSites,
        profile.requestInProgress,
        productKey,
        setShowSitePicker
    ]);

    return isNewUser ? (
        <>
            <div
                data-testid="adg4-site-name-input"
                css={[
                    inputAndLabelStyles,
                    isOnDarkBackground && darkBackgroundInputAndLabelStyles
                ]}
            >
                <InputSiteName />
            </div>
            <div
                css={[
                    suggestionStyles,
                    isOnDarkBackground && darkBackgroundTextStyles
                ]}
            >
                <FormattedMessage
                    id="BxpUiSignup.nameSite.inputSiteNameMessage"
                    defaultMessage="This site name is just a suggestion. Feel free to change to something your team will recognize."
                />
            </div>
        </>
    ) : (
        <>
            {showSitePicker ? (
                <div
                    data-testid="adg4-site-picker"
                    css={[
                        inputAndLabelStyles,
                        isOnDarkBackground && darkBackgroundInputAndLabelStyles
                    ]}
                >
                    {productKey === ProductKey.CONFLUENCE ? (
                        <WorkspacePickerField />
                    ) : (
                        <SitePickerField />
                    )}
                </div>
            ) : (
                <div
                    css={[
                        inputAndLabelStyles,
                        isOnDarkBackground && darkBackgroundInputAndLabelStyles
                    ]}
                    data-testid="adg4-site-name-input"
                >
                    {productKey === ProductKey.CONFLUENCE ? (
                        <InputWorkspaceName />
                    ) : (
                        <InputSiteName />
                    )}
                </div>
            )}
            {hasAvailableSites && (
                <div data-testid="adg4-available-sites-button">
                    <ArrowLinkButton
                        text={toggleYourSiteButtonText}
                        onClick={() => setShowSitePicker(!showSitePicker)}
                        isOnDarkBackground={isOnDarkBackground}
                    />
                </div>
            )}
        </>
    );
};

type Adg4SiteNameFormProps = {
    showSubheader?: boolean;
    isOnDarkBackground?: boolean;
};

export const Adg4SiteNameForm = ({
    showSubheader = true,
    isOnDarkBackground = false
}: Adg4SiteNameFormProps) => {
    const intl = useIntl();
    const {
        userInfo: { name },
        isNewUser,
        isSubmittingState: [isSubmitting],
        activateSite,
        productData: { productKey }
    } = useSiteNameContext();

    let subHeaderText;
    if (isNewUser) {
        subHeaderText = intl.formatMessage({
            id: "BxpUiSignup.nameSite.cobrandedNewUserSubHeader",
            defaultMessage:
                "Sites are the shared space where people organize teams, work, and projects."
        });
    } else if (productKey === ProductKey.CONFLUENCE) {
        subHeaderText = <Jfind1315SubHeading returnString={true} />;
    } else {
        subHeaderText = <Jfind1249SubHeading returnString={true} />;
    }

    const siteNameAgreeButton = intl.formatMessage({
        id: "BxpUiSignup.nameSite.continueButton",
        defaultMessage: "Continue"
    });

    return (
        <div css={containerCss} data-testid="adg4-site-name-form">
            <div css={headerGroupCss}>
                <h1
                    css={[
                        headerCss,
                        isOnDarkBackground && darkBackgroundTextStyles
                    ]}
                >
                    {isNewUser ? (
                        <span css={accentImageSpanCss}>
                            <FormattedMessage
                                id="signupForm.accountCreation.cobrandedLoggedOutHeader"
                                defaultMessage={`Create a <accentImage>site</accentImage>`}
                                values={{
                                    accentImage: (text: ReactNode) => (
                                        <>{text}</>
                                    )
                                }}
                            />
                            <svg
                                aria-hidden="true"
                                css={accentImageCss}
                                fill="#FCA700"
                                viewBox="0 0 57 52"
                            >
                                <path d="M43.9096 31.989C44.2964 32.1979 50.1032 30.4768 48.3209 29.7729C47.7086 29.434 47.1876 29.537 47.0824 30.1563C46.7711 29.0152 45.8277 30.6914 45.2428 30.3744C45.338 29.5426 43.9174 31.4167 44.1295 30.5384C37.6167 30.3944 25.6493 39.724 40.8255 33.2041C39.218 33.0188 40.6554 32.5298 41.3794 32.6287C40.4962 33.7793 43.3258 32.5457 43.8251 32.1261C42.9181 32.2503 44.3277 31.315 43.9065 31.9866L43.9096 31.989Z" />
                                <path d="M31.1742 23.4941C32.0038 23.3481 34.8483 17.2583 35.2257 16.6666C35.7963 15.2515 33.8616 15.3637 33.6797 16.5353C33.5454 16.7474 33.1876 18.4271 32.7181 17.7781C31.123 18.3209 31.2287 21.2383 29.8684 21.5744C30.1331 21.9302 29.6966 22.4398 29.8375 22.6819C29.4217 22.6843 29.2562 23.2605 29.3603 23.2987C27.7702 24.9228 30.1396 26.3595 30.8463 24.2051C30.9274 24.2847 31.3634 23.8695 31.1748 23.4996L31.1742 23.4941Z" />
                                <path d="M8.6666 15.2222C8.02491 16.2514 7.96222 15.2487 8.28065 14.7497C9.05234 15.52 8.82473 13.5428 8.54045 13.0616C8.52243 13.2717 8.17 13.3446 8.18227 12.9105C9.08124 13.2088 9.17211 7.72002 7.35544 10.3155C7.36042 10.4093 8.06672 10.4491 7.84889 10.5596C6.77715 10.3778 7.53675 11.2383 7.62227 11.4811C7.06702 11.477 7.58662 11.707 7.35778 11.7688C6.60614 11.4733 7.8784 12.8725 7.13341 12.5375C6.01834 13.7974 7.55678 16.2818 6.53876 17.2452C7.79737 17.9053 5.99454 21.9334 8.35492 20.3752C8.8673 18.6393 9.14873 17.2179 8.6666 15.2222Z" />
                                <path d="M21.257 4.74268C19.536 4.31212 19.9685 8.08877 19.2823 9.11742C18.2988 10.3626 15.0337 23.7231 18.3519 20.2458C19.306 16.1939 20.7963 13.0387 21.3321 7.97975C21.4791 7.16432 22.4539 4.63025 21.257 4.74268Z" />
                                <path d="M37.0965 44.953C36.1393 46.3589 34.6992 44.6262 33.7861 45.783C32.4207 48.0004 45.1215 48.6579 44.6732 46.3948C44.025 44.501 38.0156 45.7761 37.0965 44.953Z" />
                            </svg>
                        </span>
                    ) : (
                        <WelcomeBackHeading name={name} />
                    )}
                </h1>
                {showSubheader && (
                    <p
                        css={[
                            headerCss,
                            isOnDarkBackground && darkBackgroundTextStyles
                        ]}
                    >
                        {subHeaderText}
                    </p>
                )}
            </div>
            <Form onSubmit={activateSite} isDisabled={false}>
                {({ formProps, submitting }) => (
                    <form {...formProps} autoComplete="off">
                        <div>
                            <div css={formWrapperCss}>
                                <Adg4SiteNameFormInputs
                                    isOnDarkBackground={isOnDarkBackground}
                                />
                            </div>
                            <div css={buttonStyles}>
                                <Adg4Button
                                    isDisabled={submitting || isSubmitting}
                                    isLoading={submitting || isSubmitting}
                                    type="submit"
                                    dataEventProps={{
                                        "data-product-key": "jira",
                                        "data-event": "clicked",
                                        "data-event-component": "linkButton",
                                        "data-event-container": "siteName",
                                        "data-label": "continue-or-agree",
                                        "data-label-english":
                                            siteNameAgreeButton,
                                        "data-track-event":
                                            "jira-site-name.site-name-form.atlas-66195.site-name"
                                    }}
                                    color={
                                        isOnDarkBackground
                                            ? "yellow"
                                            : undefined
                                    }
                                >
                                    {siteNameAgreeButton}
                                </Adg4Button>
                            </div>
                        </div>
                    </form>
                )}
            </Form>
        </div>
    );
};

const containerCss = css({
    display: "flex",
    flexDirection: "column"
});

const headerGroupCss = css({
    display: "grid",
    gap: token("space.100")
});

const headerCss = css({
    margin: 0,
    textAlign: "center"
});

const darkBackgroundTextStyles = css({
    color: `${wacToken("color.neutral.light0")} !important`
});

const formWrapperCss = css({
    margin: "24px 0px"
});

const suggestionStyles = css({
    color: wacToken("color.primary.black"),
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "12px",
    lineHeight: "18px",
    fontWeight: "400",
    margin: "0.5rem 0 0"
});

const buttonStyles = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["button"]: {
        width: "100%"
    }
});

const inputAndLabelStyles = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["label + div > div:first-of-type, div[data-disabled]"]: {
        borderRadius: "21px"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["input, label + div > div > div"]: {
        paddingInlineStart: "12px !important"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["label > span"]: {
        color: wacToken("color.primary.black"),
        fontFamily: wacToken("font.charlie.text"),
        fontSize: "14px",
        fontWeight: "400",
        lineHeight: "20px"
    }
});

const darkBackgroundInputAndLabelStyles = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["label > span"]: {
        color: `${wacToken("color.neutral.light0")} !important`
    }
});

const accentImageSpanCss = css({
    position: "relative"
});

const accentImageCss = css({
    height: "52px",
    insetBlockStart: "-14px",
    insetInlineEnd: "-33px",
    position: "absolute",
    width: "57px"
});
